import type { EventBus } from '@/core/EventBus';
import type { CurrencyManager } from '@/economy/CurrencyManager';
import {
  RULER_DEFINITIONS,
  getRulerDefinition,
  type RulerDefinition,
  type RulerPassive,
} from './RulerDefinitions';

/**
 * Ruler events
 */
export const RulerEvents = {
  RULER_SELECTED: 'ruler:selected',
  PASSIVE_APPLIED: 'ruler:passiveApplied',
} as const;

export interface RulerSelectedEvent {
  playerId: number;
  rulerId: string;
  rulerName: string;
}

/**
 * Manages ruler selection and passive effects
 */
export class RulerSystem {
  private eventBus: EventBus;
  private currencyManager: CurrencyManager;

  private player1Ruler: RulerDefinition | null = null;
  private player2Ruler: RulerDefinition | null = null;

  constructor(eventBus: EventBus, currencyManager: CurrencyManager) {
    this.eventBus = eventBus;
    this.currencyManager = currencyManager;
  }

  /**
   * Select a ruler for a player
   */
  selectRuler(playerId: number, rulerId: string): boolean {
    const ruler = getRulerDefinition(rulerId);
    if (!ruler) {
      console.error(`Unknown ruler: ${rulerId}`);
      return false;
    }

    if (playerId === 1) {
      this.player1Ruler = ruler;
    } else {
      this.player2Ruler = ruler;
    }

    this.eventBus.emit(RulerEvents.RULER_SELECTED, {
      playerId,
      rulerId,
      rulerName: ruler.name,
    } as RulerSelectedEvent);

    console.log(`Player ${playerId} selected ruler: ${ruler.name}, ${ruler.title}`);

    return true;
  }

  /**
   * Get selected ruler for a player
   */
  getRuler(playerId: number): RulerDefinition | null {
    return playerId === 1 ? this.player1Ruler : this.player2Ruler;
  }

  /**
   * Get passive for a player
   */
  getPassive(playerId: number): RulerPassive | null {
    const ruler = this.getRuler(playerId);
    return ruler?.passive ?? null;
  }

  /**
   * Get team stat bonuses from ruler passive
   */
  getTeamBonuses(playerId: number): {
    attackPercent: number;
    defensePercent: number;
    healthPercent: number;
    speedPercent: number;
    attackSpeedPercent: number;
    abilityPowerPercent: number;
  } {
    const defaults = {
      attackPercent: 0,
      defensePercent: 0,
      healthPercent: 0,
      speedPercent: 0,
      attackSpeedPercent: 0,
      abilityPowerPercent: 0,
    };

    const passive = this.getPassive(playerId);
    if (!passive?.teamBonuses) return defaults;

    return {
      attackPercent: passive.teamBonuses.attackPercent ?? 0,
      defensePercent: passive.teamBonuses.defensePercent ?? 0,
      healthPercent: passive.teamBonuses.healthPercent ?? 0,
      speedPercent: passive.teamBonuses.speedPercent ?? 0,
      attackSpeedPercent: passive.teamBonuses.attackSpeedPercent ?? 0,
      abilityPowerPercent: passive.teamBonuses.abilityPowerPercent ?? 0,
    };
  }

  /**
   * Get economy bonuses from ruler passive
   */
  getEconomyBonuses(playerId: number): {
    extraGoldPerRound: number;
    interestBonus: number;
    rerollDiscount: number;
  } {
    const defaults = {
      extraGoldPerRound: 0,
      interestBonus: 0,
      rerollDiscount: 0,
    };

    const passive = this.getPassive(playerId);
    if (!passive?.economyBonuses) return defaults;

    return {
      extraGoldPerRound: passive.economyBonuses.extraGoldPerRound ?? 0,
      interestBonus: passive.economyBonuses.interestBonus ?? 0,
      rerollDiscount: passive.economyBonuses.rerollDiscount ?? 0,
    };
  }

  /**
   * Get special effects from ruler passive
   */
  getSpecialEffects(playerId: number): string[] {
    const passive = this.getPassive(playerId);
    return passive?.specialEffects ?? [];
  }

  /**
   * Check if player has a specific special effect
   */
  hasSpecialEffect(playerId: number, effectId: string): boolean {
    return this.getSpecialEffects(playerId).includes(effectId);
  }

  /**
   * Apply economy bonuses at round start
   */
  applyRoundStartBonuses(): void {
    // Give extra gold from ruler passives
    for (const playerId of [1, 2]) {
      const bonuses = this.getEconomyBonuses(playerId);
      if (bonuses.extraGoldPerRound > 0) {
        this.currencyManager.addGold(
          playerId,
          bonuses.extraGoldPerRound,
          'Ruler bonus'
        );
      }
    }
  }

  /**
   * Get effective reroll cost for a player (after ruler discounts)
   */
  getEffectiveRerollCost(playerId: number, baseCost: number): number {
    const bonuses = this.getEconomyBonuses(playerId);
    return Math.max(0, baseCost - bonuses.rerollDiscount);
  }

  /**
   * Check if both players have selected rulers
   */
  bothRulersSelected(): boolean {
    return this.player1Ruler !== null && this.player2Ruler !== null;
  }

  /**
   * Get all available rulers for selection
   */
  getAvailableRulers(): RulerDefinition[] {
    return Object.values(RULER_DEFINITIONS);
  }

  /**
   * Reset ruler selections
   */
  reset(): void {
    this.player1Ruler = null;
    this.player2Ruler = null;
  }
}
